import clsx from 'clsx'
import axios, {CancelTokenSource} from 'axios'
import {Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState} from 'react'
import Select, {FormatOptionLabelMeta, GroupBase, SingleValue} from 'react-select'
import type {Stuffid} from '../../../../../stuffid-management/stuffids-list/core/_models'
import {useDebounce} from '../../../../../../../../_metronic/helpers'
import {FieldHelpLabel} from '../../../../../../../../_metronic/helpers/components/FieldHelp'
import {
  categoryChoiceParams,
  categoryLabelPath,
  describeCategoryChoice,
  formatStuffIdNumber,
  isSearchableStuffIdTerm,
  loadStuffIdGroups,
  normalizeStuffIdSearch,
  parseStuffIdTotal,
  StuffIdCategoryChoice,
  StuffIdGroup,
  stuffIdSearchErrorMessage,
  STUFFID_SEARCH_URL,
} from '../../../../../stuffid-management/stuffidCatalog'
import {useAuth} from '../../../../../../auth'
import {useIsOnPremises} from '../../../../../../auth/core/useOnPremises'
import {businessSettingsPath} from '../../../../../../online-payment/core/businessSettingsPath'
import {hidesPayments} from '../../../../../../install-app/core/androidApp'
import {readCategoryLock} from '../../../../../stuffid-management/stuffidAccessApi'
import './selectProduct.css'

type props = {
  stuffidSelected: {
    stuffidInfo: Stuffid | undefined | null
    setStuffidInfo: Dispatch<SetStateAction<Stuffid | undefined | null>>
  }
}

// «g:12» a whole group, «t:34» one subgroup, «» every group.
type CategoryOption = {
  value: string
  name: string
  groupName: string
  count?: number | null
  isGroup: boolean
}

type CategoryOptionGroup = GroupBase<CategoryOption> & {label: string}

const PAGE_SIZE = 30
const SEARCH_DELAY_MS = 250
const SEARCH_TIMEOUT_MS = 30000

const choiceOf = (value: string): StuffIdCategoryChoice => {
  const [kind, id] = value.split(':')
  const number = Number(id)
  if (!(number > 0)) {
    return {}
  }
  return kind === 't' ? {tagId: number} : {groupId: number}
}

const toCategoryOptions = (groups: StuffIdGroup[]): CategoryOptionGroup[] =>
  groups.map((group) => ({
    label: group.name,
    options: [
      {
        value: `g:${group.id}`,
        name: `همه «${group.name}»`,
        groupName: group.name,
        count: group.count,
        isGroup: true,
      },
      ...group.tags.map((tag) => ({
        value: `t:${tag.id}`,
        name: tag.name,
        groupName: group.name,
        count: tag.count,
        isGroup: false,
      })),
    ],
  }))

// Typing a group's name keeps all of its subgroups in the list.
const matchesCategory = (option: {data: CategoryOption}, input: string) => {
  const term = normalizeStuffIdSearch(input).trim()
  return !term || option.data.name.includes(term) || option.data.groupName.includes(term)
}

const formatCategoryOption = (
  option: CategoryOption,
  {context}: FormatOptionLabelMeta<CategoryOption>
) =>
  context === 'value' ? (
    // Plain inline text: react-select clips a flex row inside the RTL value container.
    <span>{option.isGroup ? option.groupName : `${option.groupName} / ${option.name}`}</span>
  ) : (
    <span className='d-flex align-items-center justify-content-between gap-3'>
      <span className={clsx(option.isGroup && 'fw-bold')}>{option.name}</span>
      {option.count != null && (
        <span className='text-gray-600 fs-7 text-nowrap' title='تعداد شناسه'>
          {formatStuffIdNumber(option.count)}
        </span>
      )}
    </span>
  )

const vatLabel = (rate?: number) =>
  Number(rate || 0) === 0 ? 'معاف از مالیات' : `مالیات ${formatStuffIdNumber(rate)}٪`

const requestPage = (
  source: CancelTokenSource,
  term: string,
  categoryValue: string,
  pageNumber: number
) =>
  axios.get(STUFFID_SEARCH_URL, {
    cancelToken: source.token,
    timeout: SEARCH_TIMEOUT_MS,
    params: {
      page: pageNumber,
      items_per_page: PAGE_SIZE,
      search: term,
      ...categoryChoiceParams(choiceOf(categoryValue)),
    },
  })

// A text search does not count its matches; it only says whether another page follows.
const readPage = (data: any) => {
  const pagination = data?.payload?.pagination
  return {
    rows: ((data?.data || []) as Stuffid[]).filter(Boolean),
    total: parseStuffIdTotal(pagination?.filterTotal),
    totalIsExact: pagination?.totalIsExact !== false,
    hasMore: pagination?.hasMore === true,
  }
}

/**
 * The goods/services ID picker of the product form: nothing is asked for until a name, a code or a
 * group is given; the groups come with their ID counts, each result shows its group, and further
 * results load in pages of thirty. The bank's own install has no ID categories (the user, 2026-10-25): the search alone.
 */
const SelectTemplate: FC<props> = ({stuffidSelected}) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, SEARCH_DELAY_MS)
  const [categoryValue, setCategoryValue] = useState('')
  const [groups, setGroups] = useState<StuffIdGroup[]>([])
  const [groupsState, setGroupsState] = useState<'loading' | 'ready' | 'failed'>('loading')
  const [items, setItems] = useState<Stuffid[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalIsExact, setTotalIsExact] = useState(true)
  const [hasMore, setHasMore] = useState(false)
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const [error, setError] = useState('')
  const [loadingMore, setLoadingMore] = useState(false)
  const [moreError, setMoreError] = useState('')
  const [retryVersion, setRetryVersion] = useState(0)
  // A search by category the business may not browse (2026-10-18): the refusal names the service to turn on.
  const [locked, setLocked] = useState(false)
  const {currentUser} = useAuth()
  const onPremises = useIsOnPremises()
  const requestRef = useRef<CancelTokenSource | null>(null)

  const typedTerm = normalizeStuffIdSearch(search).trim()
  const termTooShort = Boolean(typedTerm) && !isSearchableStuffIdTerm(typedTerm)
  const debouncedTerm = normalizeStuffIdSearch(debouncedSearch || '').trim()
  const searchTerm = isSearchableStuffIdTerm(debouncedTerm) ? debouncedTerm : ''

  const categoryOptions = useMemo(() => toCategoryOptions(groups), [groups])
  const selectedOption = useMemo(
    () =>
      categoryOptions
        .flatMap((group) => group.options)
        .find((option) => option.value === categoryValue) || null,
    [categoryOptions, categoryValue]
  )
  const categoryName = describeCategoryChoice(groups, choiceOf(categoryValue))
  const startGroups = useMemo(
    () => [...groups].sort((first, second) => Number(second.count || 0) - Number(first.count || 0)),
    [groups]
  )

  useEffect(() => {
    if (onPremises) {
      setGroups([])
      setGroupsState('ready')
      return
    }
    let alive = true
    loadStuffIdGroups()
      .then((tree) => {
        if (alive) {
          setGroups(tree)
          setGroupsState('ready')
        }
      })
      .catch(() => {
        if (alive) {
          setGroupsState('failed')
        }
      })
    return () => {
      alive = false
    }
  }, [onPremises])

  useEffect(() => () => requestRef.current?.cancel('Component unmounted.'), [])

  useEffect(() => {
    requestRef.current?.cancel('search changed')
    requestRef.current = null
    setLoadingMore(false)
    setMoreError('')
    setError('')

    if (!searchTerm && !categoryValue) {
      setItems([])
      setStatus('idle')
      return
    }

    const source = axios.CancelToken.source()
    requestRef.current = source
    setStatus('loading')
    requestPage(source, searchTerm, categoryValue, 1)
      .then((response) => {
        if (requestRef.current !== source) {
          return
        }
        const result = readPage(response.data)
        setItems(result.rows)
        setPage(1)
        setTotal(result.total)
        setTotalIsExact(result.totalIsExact)
        setHasMore(result.hasMore)
        setStatus('ready')
      })
      .catch((requestError) => {
        if (axios.isCancel(requestError) || requestRef.current !== source) {
          return
        }
        setItems([])
        const lock = readCategoryLock(requestError)
        setLocked(Boolean(lock))
        setError(lock ? lock.message : stuffIdSearchErrorMessage(requestError))
        setStatus('error')
      })
  }, [searchTerm, categoryValue, retryVersion])

  const loadMore = () => {
    const source = axios.CancelToken.source()
    requestRef.current = source
    setLoadingMore(true)
    setMoreError('')
    requestPage(source, searchTerm, categoryValue, page + 1)
      .then((response) => {
        if (requestRef.current !== source) {
          return
        }
        const result = readPage(response.data)
        setItems((current) => [...current, ...result.rows])
        setPage((current) => current + 1)
        setTotal(result.total)
        setTotalIsExact(result.totalIsExact)
        setHasMore(result.hasMore)
      })
      .catch((requestError) => {
        if (axios.isCancel(requestError) || requestRef.current !== source) {
          return
        }
        setMoreError(stuffIdSearchErrorMessage(requestError))
      })
      .finally(() => {
        if (requestRef.current === source) {
          setLoadingMore(false)
        }
      })
  }

  const canLoadMore = status === 'ready' && (totalIsExact ? items.length < total : hasMore)
  const resultCount =
    status !== 'ready'
      ? ''
      : totalIsExact
      ? `${formatStuffIdNumber(total)} شناسه`
      : `بیش از ${formatStuffIdNumber(items.length)} شناسه`
  const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined

  return (
    <div className='mp-stuffid-picker'>
      <div className='mp-stuffid-picker-toolbar'>
        <div>
          <h3 className='mp-stuffid-picker-title'>
            <FieldHelpLabel label='انتخاب شناسه مالیاتی' helpKey='body[].sstid' />
          </h3>
          <div className='mp-stuffid-picker-subtitle'>
            {onPremises
              ? 'نام کالا/خدمت یا شناسه ۱۳ رقمی را بنویسید.'
              : 'نام کالا/خدمت یا شناسه ۱۳ رقمی را بنویسید؛ با انتخاب گروه، نتایج محدودتر می‌شود.'}
          </div>
        </div>
      </div>

      <div className='mp-stuffid-filter-box'>
        <div className='row g-3'>
          <div className={onPremises ? 'col-12' : 'col-lg-7'}>
            <label className='form-label fw-semibold' htmlFor='stuffid-template-search'>
              جستجوی شناسه کالا یا خدمت
            </label>
            <div className='mp-stuffid-search'>
              <i className='las la-search mp-stuffid-search-icon' aria-hidden='true'></i>
              <input
                id='stuffid-template-search'
                className='form-control form-control-lg'
                type='search'
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder='مثلاً «کابل برق» یا شناسه ۱۳ رقمی'
                autoComplete='off'
                dir='auto'
              />
            </div>
            {termTooShort && (
              <div className='form-text'>برای جستجو دست‌کم ۲ حرف یا ۳ رقم بنویسید.</div>
            )}
          </div>
          {!onPremises && (
            <div className='col-lg-5 mp-stuffid-category'>
              <label className='form-label fw-semibold' htmlFor='stuffid-template-category'>
                گروه کالا یا خدمت
              </label>
              <Select<CategoryOption, false, CategoryOptionGroup>
                inputId='stuffid-template-category'
                classNamePrefix='select'
                isRtl
                isClearable
                isLoading={groupsState === 'loading'}
                isDisabled={groupsState === 'failed'}
                placeholder={groupsState === 'failed' ? 'گروه‌ها در دسترس نیست' : 'همه گروه‌ها'}
                noOptionsMessage={() => 'گروهی با این نام پیدا نشد.'}
                loadingMessage={() => 'در حال دریافت گروه‌ها...'}
                options={categoryOptions}
                value={selectedOption}
                onChange={(option: SingleValue<CategoryOption>) =>
                  setCategoryValue(option ? option.value : '')
                }
                filterOption={matchesCategory}
                formatOptionLabel={formatCategoryOption}
                getOptionLabel={(option) => `${option.groupName} ${option.name}`}
                getOptionValue={(option) => option.value}
                menuPosition='fixed'
                menuPortalTarget={menuPortalTarget}
                styles={{
                  menuPortal: (base) => ({...base, zIndex: 10000}),
                  menu: (base) => ({...base, zIndex: 10000}),
                }}
              />
            </div>
          )}
        </div>
      </div>

      {status === 'idle' ? (
        <div className='mp-stuffid-start'>
          <div className='mp-stuffid-start-copy'>
            <i className='las la-barcode' aria-hidden='true'></i>
            <span>
              <strong>شناسه کالا یا خدمت را پیدا کنید</strong>
              <span>
                {onPremises
                  ? 'نام یا شناسه را بنویسید.'
                  : 'نام یا شناسه را بنویسید، یا از یکی از گروه‌ها شروع کنید.'}
              </span>
            </span>
          </div>
          {startGroups.length > 0 && (
            <div className='mp-stuffid-start-groups' aria-label='گروه‌های کالا و خدمت'>
              {startGroups.map((group) => (
                <button
                  key={group.id}
                  type='button'
                  className='mp-stuffid-chip'
                  onClick={() => setCategoryValue(`g:${group.id}`)}
                >
                  <span>{group.name}</span>
                  {group.count != null && (
                    <span className='mp-stuffid-chip-count'>
                      {formatStuffIdNumber(group.count)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className='mp-stuffid-results-head' aria-live='polite'>
            <span>{status === 'loading' ? 'در حال جستجو...' : resultCount || 'نتایج'}</span>
            {categoryName && (
              <span className='badge badge-light-primary mp-stuffid-results-scope'>
                {categoryName}
              </span>
            )}
          </div>

          {status === 'loading' && (
            <div className='mp-stuffid-state'>
              <span
                className='spinner-border spinner-border-sm text-primary'
                aria-hidden='true'
              ></span>
              در حال جستجو...
            </div>
          )}

          {status === 'error' && (
            <div className='mp-stuffid-state mp-stuffid-empty' role='alert'>
              <i className='las la-exclamation-circle' aria-hidden='true'></i>
              <strong>{error}</strong>
              {locked && currentUser?.cpi && !hidesPayments() ? (
                <a
                  className='btn btn-sm btn-light'
                  href={businessSettingsPath(currentUser.cpi, 'services-overview')}
                  target='_blank'
                  rel='noreferrer'
                >
                  سرویس‌ها و تعرفه‌ها
                </a>
              ) : null}
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={() => setRetryVersion((current) => current + 1)}
              >
                تلاش مجدد
              </button>
            </div>
          )}

          {status === 'ready' && items.length > 0 && (
            <>
              <div className='mp-stuffid-result-grid'>
                {items.map((item, index) => (
                  <button
                    type='button'
                    key={`${item.cd}:${item.ix ?? index}`}
                    className='mp-stuffid-result-card'
                    onClick={() => stuffidSelected.setStuffidInfo(item)}
                  >
                    <span className='mp-stuffid-result-name'>{item.ds || 'بدون شرح'}</span>
                    <span className='mp-stuffid-result-meta'>
                      <span className='mp-stuffid-result-code' dir='ltr'>
                        {item.cd}
                      </span>
                      {item.tp && <span className='badge badge-light-info'>{item.tp}</span>}
                      <span
                        className={`badge ${
                          Number(item.rt || 0) === 0 ? 'badge-light-success' : 'badge-light-primary'
                        }`}
                      >
                        {vatLabel(item.rt)}
                      </span>
                    </span>
                    {!onPremises && item.tg && item.tg.length > 0 && (
                      <span className='mp-stuffid-result-group'>
                        <i className='las la-folder' aria-hidden='true'></i>
                        {categoryLabelPath(item.tg[0])}
                      </span>
                    )}
                    <span className='mp-stuffid-result-action'>
                      انتخاب
                      <i className='las la-angle-left' aria-hidden='true'></i>
                    </span>
                  </button>
                ))}
              </div>

              {canLoadMore && (
                <div className='mp-stuffid-more'>
                  <button
                    type='button'
                    className='btn btn-light-primary'
                    onClick={loadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore && (
                      <span
                        className='spinner-border spinner-border-sm me-2'
                        aria-hidden='true'
                      ></span>
                    )}
                    نمایش نتایج بیشتر
                  </button>
                  {moreError && <div className='text-danger fs-7 mt-2'>{moreError}</div>}
                </div>
              )}
            </>
          )}

          {status === 'ready' && items.length === 0 && (
            <div className='mp-stuffid-state mp-stuffid-empty'>
              <i className='las la-search' aria-hidden='true'></i>
              <strong>نتیجه‌ای پیدا نشد</strong>
              <span>
                {categoryValue && searchTerm
                  ? 'این عبارت در گروه انتخاب‌شده نیست؛ گروه را بردارید تا در همه شناسه‌ها جستجو شود.'
                  : /^\d{13}$/.test(searchTerm)
                  ? 'این شناسه در فهرست شناسه‌های سامانه مؤدیان پیدا نشد؛ رقم‌ها را بررسی کنید.'
                  : 'عبارت کوتاه‌تر یا نام دیگری از کالا/خدمت را امتحان کنید.'}
              </span>
              {categoryValue && searchTerm && (
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary'
                  onClick={() => setCategoryValue('')}
                >
                  جستجو در همه گروه‌ها
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export {SelectTemplate}
